import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, setDoc, deleteDoc, collection, getDocs } from 'firebase/firestore';
import { auth, db } from './firebase';

// Import all screens
import CaseSyncHub from './CaseSyncHub';
import ComplianceDashboard from './ComplianceDashboard';
import GodModeCalendar from './GodModeCalendar';
import VocationalTracker from './VocationalTracker';
import ArcHub from './ArcHub'; 
import ProgressMonitor from './ProgressMonitor'; 
import CaseloadManager from './CaseloadManager'; 
import StaffManager from './StaffManager'; 
import PlatformDashboard from './PlatformDashboard'; 
import TimesheetManager from './TimesheetManager';
import ProfileSettings from './ProfileSettings'; 
import SchoolManager from './SchoolManager';
import BehaviorPortal from './BehaviorPortal'; 
import BuildingRubricBuilder from './BuildingRubricBuilder';
import StaffBatchImporter from './StaffBatchImporter'; // <-- NEW IMPORT

function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // The Bulletproof URL Sniffer
  const isHqMode = window.location.pathname.includes('/hq');

  // Login / Claim State
  const [isClaiming, setIsClaiming] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  // Tenant Selection State
  const [districts, setDistricts] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(localStorage.getItem('activeTenant') || '');

  // Navigation State
  const [currentView, setCurrentView] = useState(isHqMode ? 'hq' : 'hub'); 

  // --- FETCH DYNAMIC DISTRICTS ---
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const snap = await getDocs(collection(db, 'tenants'));
        const activeDistricts = snap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(district => district.status === 'Active');
        
        setDistricts(activeDistricts);
      } catch (error) {
        console.error("Failed to load districts:", error);
      }
    };
    
    fetchDistricts();
  }, []);

  // --- AUTHENTICATION LISTENER ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        
        // --- HQ BACKDOOR AUTHENTICATION ---
        if (isHqMode) {
          try {
            const adminDoc = await getDoc(doc(db, 'platformAdmins', currentUser.email.toLowerCase()));
            if (adminDoc.exists()) {
              setUser(currentUser);
              setUserProfile({ role: 'PlatformAdmin' });
              setCurrentView('hq');
            } else {
              await signOut(auth);
              setLoginError("Classified Area. Platform Admins only.");
              setUser(null);
            }
          } catch (error) {
            console.error("HQ Auth Error:", error);
            setLoginError("Error verifying security clearance.");
          }
        } 
        // --- STANDARD DISTRICT AUTHENTICATION ---
        else {
          const tenantToUse = localStorage.getItem('activeTenant');
          if (tenantToUse) {
            try {
              const userDoc = await getDoc(doc(db, 'districts', tenantToUse, 'users', currentUser.uid));
              if (userDoc.exists()) {
                const profileData = userDoc.data();
                
                setUser(currentUser);
                setUserProfile(profileData);
                setSelectedTenant(tenantToUse);

                // --- NEW ROUTING BOUNCER ---
                if (profileData.role === 'Gen Ed') {
                  setCurrentView('behavior'); // Bypass the hub completely
                } else if (currentView === 'behavior') {
                  setCurrentView('hub'); // Reset if a SPED admin somehow got stuck
                }

              } else {
                await signOut(auth);
                setLoginError("You do not have an active account in this district.");
                setUser(null);
              }
            } catch (error) {
              console.error("Error fetching user profile:", error);
              setLoginError("Error connecting to the district vault.");
            }
          } else {
             await signOut(auth);
          }
        }
      } else {
        setUser(null);
        setUserProfile(null);
        setCurrentView(isHqMode ? 'hq' : 'hub'); 
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, [isHqMode]); // React rule: include external variables

  // --- SIGN IN LOGIC ---
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      if (isHqMode) {
        auth.tenantId = null; 
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        if (!selectedTenant) {
          setLoginError("Please select your district before signing in.");
          return;
        }
        localStorage.setItem('activeTenant', selectedTenant);
        auth.tenantId = selectedTenant; 
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (error) {
      console.error(error); 
      setLoginError("Invalid email or password.");
    }
  };

  // --- CLAIM ACCOUNT (WHITELIST HANDSHAKE) ---
  const handleClaimAccount = async (e) => {
    e.preventDefault();
    setLoginError('');

    if (isHqMode) {
       setLoginError("Account claiming is not permitted at HQ.");
       return;
    }

    if (!selectedTenant) {
      setLoginError("Please select your district to claim your account.");
      return;
    }

    try {
      auth.tenantId = selectedTenant; 
      const cleanEmail = email.toLowerCase().trim();
      
      const whitelistRef = doc(db, 'districts', selectedTenant, 'users', cleanEmail);
      const whitelistSnap = await getDoc(whitelistRef);

      if (!whitelistSnap.exists()) {
        setLoginError("Email not found on the district whitelist. Contact your CaseSync Administrator.");
        return;
      }

      const userData = whitelistSnap.data();
      if (userData.status !== 'Pending Registration') {
        setLoginError("This account has already been claimed. Please return to standard Sign In.");
        return;
      }

      const userCred = await createUserWithEmailAndPassword(auth, cleanEmail, password);

      await setDoc(doc(db, 'districts', selectedTenant, 'users', userCred.user.uid), {
        ...userData,
        status: 'Active',
        uid: userCred.user.uid
      });

      await deleteDoc(whitelistRef);
      localStorage.setItem('activeTenant', selectedTenant);

    } catch (error) {
      console.error(error);
      if (error.code === 'auth/email-already-in-use') {
        setLoginError("This email is already registered. Please sign in normally.");
      } else if (error.code === 'auth/weak-password') {
        setLoginError("Password should be at least 6 characters.");
      } else {
        setLoginError("Failed to claim account. Please check your information.");
      }
    }
  };

  // --- PASSWORD RESET FLOW ---
  const handleForgotPassword = async () => {
    setLoginError('');
    if (!email) {
      setLoginError("Please enter your email address first.");
      return;
    }
    
    if (!isHqMode && !selectedTenant) {
      setLoginError("Please select your district first.");
      return;
    }

    try {
      if (!isHqMode) {
        auth.tenantId = selectedTenant;
      }
      
      await sendPasswordResetEmail(auth, email);
      setLoginError("Password reset email sent! Check your inbox."); 
    } catch (error) {
      console.error(error);
      setLoginError("Error sending reset email. Verify your email and district.");
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'sans-serif' }}>Loading CaseSync OS...</div>;
  }

  // --- NOT LOGGED IN: Show Login Screen ---
  if (!user) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: isHqMode ? '#111' : '#f4f7f6', fontFamily: 'sans-serif' }}>
        <div style={{ backgroundColor: isHqMode ? '#222' : '#fff', padding: '50px 40px', borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', width: '100%', maxWidth: '450px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          
          <img 
            src="/casesync-logo.png" 
            alt="CaseSync Logo" 
            style={{ width: '100%', maxWidth: '280px', height: 'auto', marginBottom: '25px', objectFit: 'contain', filter: isHqMode ? 'grayscale(100%) brightness(200%)' : 'none' }} 
          />
          
          <h2 style={{ color: isHqMode ? '#fff' : '#003366', margin: '0 0 8px 0', textAlign: 'center', fontSize: '24px' }}>
            {isHqMode ? 'CaseSync HQ' : 'CaseSync OS'}
          </h2>
          <p style={{ color: isHqMode ? '#888' : '#666', textAlign: 'center', marginBottom: '35px', fontSize: '15px' }}>
            {isHqMode ? 'Authorized personnel only.' : (isClaiming ? 'Complete your staff registration' : 'Sign in to your district portal')}
          </p>
          
          {loginError && <div style={{ width: '100%', color: loginError.includes('sent') ? '#28a745' : '#ba0c2f', backgroundColor: loginError.includes('sent') ? 'rgba(40, 167, 69, 0.1)' : (isHqMode ? 'rgba(186, 12, 47, 0.2)' : '#ffe6e6'), padding: '10px', borderRadius: '4px', marginBottom: '20px', fontSize: '14px', textAlign: 'center', fontWeight: 'bold', boxSizing: 'border-box' }}>{loginError}</div>}
          
          <form onSubmit={isClaiming ? handleClaimAccount : handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '100%' }}>
            
            {!isHqMode && (
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>District</label>
                <select 
                  value={selectedTenant}
                  onChange={(e) => setSelectedTenant(e.target.value)}
                  style={{ width: '100%', padding: '12px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box', backgroundColor: '#fff', fontSize: '15px' }}
                  required
                >
                  <option value="">-- Select Your District --</option>
                  {districts.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px', color: isHqMode ? '#ccc' : '#333' }}>
                {isHqMode ? 'Admin Email' : 'District Email'}
              </label>
              <input 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                style={{ width: '100%', padding: '12px', borderRadius: '4px', border: isHqMode ? '1px solid #444' : '1px solid #ccc', backgroundColor: isHqMode ? '#111' : '#fff', color: isHqMode ? '#fff' : '#000', boxSizing: 'border-box', fontSize: '15px' }}
                required 
              />
            </div>
            
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px', color: isHqMode ? '#ccc' : '#333' }}>
                {isClaiming ? 'Create Password' : 'Password'}
              </label>
              <input 
                type="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                style={{ width: '100%', padding: '12px', borderRadius: '4px', border: isHqMode ? '1px solid #444' : '1px solid #ccc', backgroundColor: isHqMode ? '#111' : '#fff', color: isHqMode ? '#fff' : '#000', boxSizing: 'border-box', fontSize: '15px' }}
                required={!isClaiming} 
              />
            </div>

            <button 
              type="submit" 
              style={{ backgroundColor: isHqMode ? '#fff' : (isClaiming ? '#28a745' : '#003366'), color: isHqMode ? '#000' : 'white', border: 'none', padding: '14px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', marginTop: '15px', fontSize: '16px', transition: 'filter 0.2s' }}
              onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(85%)'}
              onMouseLeave={(e) => e.currentTarget.style.filter = 'brightness(100%)'}
            >
              {isHqMode ? 'Access HQ' : (isClaiming ? 'Claim Account' : 'Sign In')}
            </button>
          </form>

          {!isClaiming && (
            <div style={{ textAlign: 'center', marginTop: '10px' }}>
              <button 
                onClick={handleForgotPassword}
                type="button"
                style={{ background: 'none', border: 'none', color: isHqMode ? '#888' : '#003366', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Forgot Password?
              </button>
            </div>
          )}

          {!isHqMode && (
            <div style={{ marginTop: '25px', textAlign: 'center', width: '100%', borderTop: '1px solid #eee', paddingTop: '20px' }}>
              <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>
                {isClaiming ? 'Already claimed your account?' : 'First time here?'}
              </p>
              <button 
                onClick={() => { setIsClaiming(!isClaiming); setLoginError(''); setEmail(''); setPassword(''); }}
                style={{ background: 'none', border: 'none', color: '#003366', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer', textDecoration: 'underline' }}
              >
                {isClaiming ? 'Return to Sign In' : 'Claim Your Account'}
              </button>
            </div>
          )}

        </div>
      </div>
    );
  }

  // --- LOGGED IN: The App Router ---
  return (
    <div>
      {/* View: Platform HQ */}
      {currentView === 'hq' && <PlatformDashboard />}

      {/* View: General Education Behavior Portal (The Walled Garden) */}
      {currentView === 'behavior' && (
        <div style={{ backgroundColor: '#f4f7f6', minHeight: '100vh' }}>
          {/* Notice: No 'Return to Hub' button here. They are locked to this view. */}
          <div style={{ backgroundColor: '#fff', padding: '15px 25px', borderBottom: '1px solid #ddd', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <img 
                src="/casesync-logo.png" 
                alt="CaseSync" 
                style={{ height: '60px', objectFit: 'contain' }} 
              />
              <button 
                onClick={() => signOut(auth)} 
                style={{ background: 'none', border: 'none', color: '#ba0c2f', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', padding: 0 }}
              >
                Sign Out
              </button>
            </div>
          </div>
          <BehaviorPortal currentUser={{ tenantId: selectedTenant, ...userProfile }} />
        </div>
      )}

      {/* View: The Waffle Hub (Blocked for Gen Ed) */}
      {currentView === 'hub' && userProfile?.role !== 'Gen Ed' && (
        <CaseSyncHub 
          userName={userProfile?.name} 
          userRole={userProfile?.role} 
          onNavigate={(appCode) => setCurrentView(appCode)} 
        />
      )}

      {/* View: User Profile Settings */}
      {currentView === 'profile' && userProfile?.role !== 'Gen Ed' && (
        <div style={{ backgroundColor: '#f4f7f6', minHeight: '100vh' }}>
          <div style={{ backgroundColor: '#fff', padding: '15px 25px', borderBottom: '1px solid #ddd', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <img 
                src="/casesync-logo.png" 
                alt="CaseSync Home" 
                onClick={() => setCurrentView('hub')}
                style={{ height: '60px', objectFit: 'contain', cursor: 'pointer', transition: 'transform 0.2s ease' }} 
              />
              <button onClick={() => setCurrentView('hub')} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', padding: 0 }}>Return to Hub</button>
            </div>
          </div>
          <ProfileSettings />
        </div>
      )}

      {/* View: The Compliance Engine */}
      {currentView === 'compliance' && (
        <div style={{ backgroundColor: '#f4f7f6', minHeight: '100vh' }}>
          <div style={{ backgroundColor: '#fff', padding: '15px 25px', borderBottom: '1px solid #ddd', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <img 
                src="/casesync-logo.png" 
                alt="CaseSync Home" 
                onClick={() => setCurrentView('hub')}
                style={{ height: '60px', objectFit: 'contain', cursor: 'pointer', transition: 'transform 0.2s ease' }} 
              />
              <button onClick={() => setCurrentView('hub')} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', padding: 0 }}>Return to Hub</button>
            </div>
          </div>
          <ComplianceDashboard tenantId={selectedTenant} />
        </div>
      )}

      {/* View: God Mode Settings */}
      {currentView === 'settings' && (
        <div style={{ backgroundColor: '#f4f7f6', minHeight: '100vh' }}>
          <div style={{ backgroundColor: '#fff', padding: '15px 25px', borderBottom: '1px solid #ddd', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <img 
                src="/casesync-logo.png" 
                alt="CaseSync Home" 
                onClick={() => setCurrentView('hub')}
                style={{ height: '60px', objectFit: 'contain', cursor: 'pointer', transition: 'transform 0.2s ease' }} 
              />
              <button onClick={() => setCurrentView('hub')} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', padding: 0 }}>Return to Hub</button>
            </div>
          </div>
          <div style={{ padding: '40px 20px', display: 'flex', flexDirection: 'column', gap: '40px', maxWidth: '1000px', margin: '0 auto' }}>
            <SchoolManager tenantId={selectedTenant} />
            <div style={{ borderBottom: '2px dashed #ccc' }}></div>
            <StaffManager tenantId={selectedTenant} />
            <div style={{ borderBottom: '2px dashed #ccc' }}></div>
            <StaffBatchImporter tenantId={selectedTenant} />
            <div style={{ borderBottom: '2px dashed #ccc' }}></div>
            <GodModeCalendar tenantId={selectedTenant} />
            <div style={{ borderBottom: '2px dashed #ccc' }}></div>
            <BuildingRubricBuilder tenantId={selectedTenant} />
          </div>
        </div>
      )}

      {/* View: Vocational Tracker */}
      {currentView === 'vocational' && (
        <div style={{ backgroundColor: '#f4f7f6', minHeight: '100vh' }}>
          <div style={{ backgroundColor: '#fff', padding: '15px 25px', borderBottom: '1px solid #ddd', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <img 
                src="/casesync-logo.png" 
                alt="CaseSync Home" 
                onClick={() => setCurrentView('hub')}
                style={{ height: '60px', objectFit: 'contain', cursor: 'pointer', transition: 'transform 0.2s ease' }} 
              />
              <button onClick={() => setCurrentView('hub')} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', padding: 0 }}>Return to Hub</button>
            </div>
          </div>
          <VocationalTracker tenantId={selectedTenant} />
        </div>
      )}

      {/* View: Progress Monitor */}
      {currentView === 'progress' && (
        <div style={{ backgroundColor: '#f4f7f6', minHeight: '100vh' }}>
          <div style={{ backgroundColor: '#fff', padding: '15px 25px', borderBottom: '1px solid #ddd', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <img 
                src="/casesync-logo.png" 
                alt="CaseSync Home" 
                onClick={() => setCurrentView('hub')}
                style={{ height: '60px', objectFit: 'contain', cursor: 'pointer', transition: 'transform 0.2s ease' }} 
              />
              <button onClick={() => setCurrentView('hub')} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', padding: 0 }}>Return to Hub</button>
            </div>
          </div>
          <ProgressMonitor tenantId={selectedTenant} />
        </div>
      )}

      {/* View: ARC Meeting Hub */}
      {currentView === 'archub' && (
        <div style={{ backgroundColor: '#f4f7f6', minHeight: '100vh' }}>
          <div style={{ backgroundColor: '#fff', padding: '15px 25px', borderBottom: '1px solid #ddd', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <img 
                src="/casesync-logo.png" 
                alt="CaseSync Home" 
                onClick={() => setCurrentView('hub')}
                style={{ height: '60px', objectFit: 'contain', cursor: 'pointer', transition: 'transform 0.2s ease' }} 
              />
              <button onClick={() => setCurrentView('hub')} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', padding: 0 }}>Return to Hub</button>
            </div>
          </div>
          <ArcHub tenantId={selectedTenant} />
        </div>
      )}

      {/* View: Caseload Manager */}
      {currentView === 'caseload' && (
        <div style={{ backgroundColor: '#f4f7f6', minHeight: '100vh' }}>
          <div style={{ backgroundColor: '#fff', padding: '15px 25px', borderBottom: '1px solid #ddd', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <img 
                src="/casesync-logo.png" 
                alt="CaseSync Home" 
                onClick={() => setCurrentView('hub')}
                style={{ height: '60px', objectFit: 'contain', cursor: 'pointer', transition: 'transform 0.2s ease' }} 
              />
              <button onClick={() => setCurrentView('hub')} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', padding: 0 }}>Return to Hub</button>
            </div>
          </div>
          <CaseloadManager tenantId={selectedTenant} />
        </div>
      )}

      {/* View: Timesheet Manager */}
      {currentView === 'timesheet' && (
        <div style={{ backgroundColor: '#f4f7f6', minHeight: '100vh' }}>
          <div style={{ backgroundColor: '#fff', padding: '15px 25px', borderBottom: '1px solid #ddd', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <img 
                src="/casesync-logo.png" 
                alt="CaseSync Home" 
                onClick={() => setCurrentView('hub')}
                style={{ height: '60px', objectFit: 'contain', cursor: 'pointer', transition: 'transform 0.2s ease' }} 
              />
              <button onClick={() => setCurrentView('hub')} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', padding: 0 }}>Return to Hub</button>
            </div>
          </div>
          <TimesheetManager tenantId={selectedTenant} />
        </div>
      )}

    </div>
  );
}

export default App;